import { z } from 'zod';

/**
 * 「かんたん更新」の対象定義・解決ロジックとワイヤ契約。
 *
 * 業種テンプレートごとに「顧客が継続的に更新したい箇所」（お知らせ・メニュー・
 * 価格・事例・写真など）を定義し、テナントのページから該当ブロックを探して
 * 編集動線へ誘導するための共通基盤。CMS 管理画面ダッシュボード（apps/cms）と
 * admin テナントダッシュボードが同じ規則を使うため、Payload に依存しない
 * 純粋ロジックとしてここに置く。ページの取得（Payload クエリ）と URL の
 * 組み立ては各アプリ側の責務。
 *
 * 会社住所・アクセス・テーマ設定のような「一度決めたらほぼ更新しない」項目は
 * 意図的に含めない — それらは従来の編集領域カードから辿る。
 *
 * admin への配信は `POST /api/v1/quick-update/entries`（service token 認証、
 * apps/cms/src/endpoints/quick-update-entries.ts）が解決済みエントリを下の
 * `QuickUpdateEntriesResponseSchema` の形で返す。admin はこれを SSO ブリッジ
 * （`/t/{slug}/edit?next=...`）経由の誘導カードに変換するだけ。
 */

export type QuickUpdateTarget = {
  id: string;
  /** 更新対象の名前（例: お知らせ・休診案内） */
  label: string;
  /** ボタンに表示する操作名（例: お知らせを編集する） */
  actionLabel: string;
  description: string;
  /** この更新対象に対応するページブロックの blockType 一覧 */
  blockTypes: readonly string[];
  /** 想定更新頻度の目安（カードに表示） */
  frequencyLabel: string;
  /** true の場合、該当ブロックがサイトに無ければカード自体を表示しない */
  optional?: boolean;
};

/** 解決に必要な最小限のページ情報。取得手段（Payload / REST）には依存しない。 */
export type QuickUpdatePageSummary = {
  id: string | number;
  title: string;
  blockTypes: readonly string[];
};

/** 対象とページの突合結果。URL の組み立て前の中間表現。 */
export type QuickUpdateMatch = {
  target: QuickUpdateTarget;
  /** 該当ブロックを含む最初のページの ID（見つからない場合は null） */
  pageId: string | null;
  /** そのページで実際に見つかった blockType（クイック投稿フォームの判定に使う） */
  blockType: string | null;
  pageTitle: string | null;
  pageCount: number;
};

const RESTAURANT_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'restaurant-menu',
    label: 'メニュー・料金',
    actionLabel: 'メニューを編集する',
    description: '料理の追加・入替えや価格改定はここから行います。',
    blockTypes: ['menu_list'],
    frequencyLabel: '週〜月1回',
  },
  {
    id: 'restaurant-hours',
    label: '営業時間・臨時休業',
    actionLabel: '営業時間を編集する',
    description: '営業時間の変更や臨時休業のお知らせを掲載します。',
    blockTypes: ['business_hours'],
    frequencyLabel: '随時',
  },
  {
    id: 'restaurant-reservation',
    label: '予約案内',
    actionLabel: '予約案内を編集する',
    description: '予約方法や受付時間の変更を反映します。',
    blockTypes: ['reservation'],
    frequencyLabel: '随時',
    optional: true,
  },
];

const BEAUTY_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'beauty-gallery',
    label: '施術写真・ギャラリー',
    actionLabel: '写真を追加する',
    description: '新しいスタイル写真やビフォーアフターを追加します。',
    blockTypes: ['gallery', 'before_after_slider'],
    frequencyLabel: '週1回〜',
  },
  {
    id: 'beauty-menu',
    label: 'メニュー・料金',
    actionLabel: 'メニューを編集する',
    description: '季節メニューの追加や料金の改定はここから行います。',
    blockTypes: ['service_menu'],
    frequencyLabel: '月1回〜',
  },
  {
    id: 'beauty-staff',
    label: 'スタッフ紹介',
    actionLabel: 'スタッフ情報を編集する',
    description: 'スタッフの入退店や紹介文の変更を反映します。',
    blockTypes: ['staff_profile'],
    frequencyLabel: '随時',
  },
  {
    id: 'beauty-booking',
    label: '予約・キャンペーン案内',
    actionLabel: '予約案内を編集する',
    description: '予約導線の文言やキャンペーン情報を変更します。',
    blockTypes: ['booking_cta'],
    frequencyLabel: '随時',
    optional: true,
  },
];

const MEDICAL_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'medical-news',
    label: 'お知らせ・休診案内',
    actionLabel: 'お知らせを編集する',
    description: '臨時休診や診療体制の変更などのお知らせを掲載します。',
    blockTypes: ['news'],
    frequencyLabel: '随時',
  },
  {
    id: 'medical-hours',
    label: '診療時間',
    actionLabel: '診療時間を編集する',
    description: '診療時間や受付時間の変更を反映します。',
    blockTypes: ['clinicHours'],
    frequencyLabel: '随時',
  },
  {
    id: 'medical-doctors',
    label: '医師・スタッフ紹介',
    actionLabel: '医師情報を編集する',
    description: '医師の着任・退任や担当医表の変更を反映します。',
    blockTypes: ['doctorProfile'],
    frequencyLabel: '随時',
  },
  {
    id: 'medical-departments',
    label: '診療内容',
    actionLabel: '診療内容を編集する',
    description: '診療科目や対応症状の説明を更新します。',
    blockTypes: ['departments'],
    frequencyLabel: '随時',
    optional: true,
  },
];

const LAW_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'law-news',
    label: 'お知らせ・コラム',
    actionLabel: 'お知らせを編集する',
    description: '事務所からのお知らせや法律コラムを掲載します。',
    blockTypes: ['lawNews'],
    frequencyLabel: '週〜月1回',
  },
  {
    id: 'law-cases',
    label: '解決事例',
    actionLabel: '事例を追加する',
    description: '新しい解決事例・実績を追加します。',
    blockTypes: ['cases'],
    frequencyLabel: '月1回〜',
  },
  {
    id: 'law-practice',
    label: '取扱業務・料金目安',
    actionLabel: '業務案内を編集する',
    description: '取扱業務の説明や料金目安を更新します。',
    blockTypes: ['practice'],
    frequencyLabel: '随時',
  },
  {
    id: 'law-office',
    label: '事務所・メンバー紹介',
    actionLabel: '事務所情報を編集する',
    description: '所属メンバーの変更や代表あいさつを更新します。',
    blockTypes: ['officeProfile'],
    frequencyLabel: '随時',
    optional: true,
  },
];

const RETAIL_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'retail-products',
    label: '商品・価格・在庫',
    actionLabel: '商品を編集する',
    description: '新商品の追加、価格や在庫表示の更新はここから行います。',
    blockTypes: ['product_grid'],
    frequencyLabel: '週1回〜',
  },
  {
    id: 'retail-promo',
    label: 'セール・キャンペーン',
    actionLabel: 'キャンペーンを編集する',
    description: 'セール告知や期間限定キャンペーンを掲載します。',
    blockTypes: ['promo'],
    frequencyLabel: '随時',
  },
  {
    id: 'retail-store',
    label: '店舗情報・営業時間',
    actionLabel: '店舗情報を編集する',
    description: '営業時間の変更や臨時休業のお知らせを反映します。',
    blockTypes: ['store_info'],
    frequencyLabel: '随時',
  },
];

const CORPORATE_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'corporate-services',
    label: 'サービス・特長',
    actionLabel: 'サービス紹介を編集する',
    description: 'サービス内容の追加や説明文・実績数値の更新を行います。',
    blockTypes: ['features'],
    frequencyLabel: '月1回〜',
  },
  {
    id: 'corporate-works',
    label: '実績・事例の写真',
    actionLabel: '写真を追加する',
    description: '施工事例や設備・実績の写真を追加します。',
    blockTypes: ['gallery'],
    frequencyLabel: '随時',
    optional: true,
  },
];

const TARGETS_BY_INDUSTRY: Record<string, readonly QuickUpdateTarget[]> = {
  beauty: BEAUTY_TARGETS,
  corporate: CORPORATE_TARGETS,
  law: LAW_TARGETS,
  medical: MEDICAL_TARGETS,
  restaurant: RESTAURANT_TARGETS,
  retail: RETAIL_TARGETS,
};

/**
 * 業種が判定できない場合のフォールバック。実際にサイト上に存在する
 * ブロックに対応するものだけを表示する（missing 案内は出さない）。
 */
const FALLBACK_TARGETS: readonly QuickUpdateTarget[] = [
  {
    id: 'generic-news',
    label: 'お知らせ',
    actionLabel: 'お知らせを編集する',
    description: 'サイトのお知らせ・新着情報を掲載します。',
    blockTypes: ['news', 'lawNews'],
    frequencyLabel: '随時',
  },
  {
    id: 'generic-menu',
    label: 'メニュー・料金',
    actionLabel: 'メニューを編集する',
    description: 'メニューの内容や料金を更新します。',
    blockTypes: ['menu_list', 'service_menu'],
    frequencyLabel: '週〜月1回',
  },
  {
    id: 'generic-products',
    label: '商品・価格・在庫',
    actionLabel: '商品を編集する',
    description: '商品の追加や価格・在庫表示を更新します。',
    blockTypes: ['product_grid'],
    frequencyLabel: '週1回〜',
  },
  {
    id: 'generic-promo',
    label: 'セール・キャンペーン',
    actionLabel: 'キャンペーンを編集する',
    description: 'セール告知や期間限定キャンペーンを掲載します。',
    blockTypes: ['promo'],
    frequencyLabel: '随時',
  },
  {
    id: 'generic-gallery',
    label: '写真ギャラリー',
    actionLabel: '写真を追加する',
    description: '新しい写真を追加してサイトを更新します。',
    blockTypes: ['gallery', 'before_after_slider'],
    frequencyLabel: '週1回〜',
  },
  {
    id: 'generic-cases',
    label: '事例・実績',
    actionLabel: '事例を追加する',
    description: '新しい事例・実績を追加します。',
    blockTypes: ['cases'],
    frequencyLabel: '月1回〜',
  },
  {
    id: 'generic-hours',
    label: '営業時間・診療時間',
    actionLabel: '営業時間を編集する',
    description: '営業時間や臨時休業のお知らせを反映します。',
    blockTypes: ['business_hours', 'clinicHours'],
    frequencyLabel: '随時',
  },
  {
    id: 'generic-people',
    label: 'スタッフ・メンバー紹介',
    actionLabel: 'スタッフ情報を編集する',
    description: 'スタッフや担当者の紹介を更新します。',
    blockTypes: ['staff_profile', 'doctorProfile'],
    frequencyLabel: '随時',
  },
  {
    id: 'generic-store',
    label: '店舗情報',
    actionLabel: '店舗情報を編集する',
    description: '店舗の営業時間やお知らせを更新します。',
    blockTypes: ['store_info'],
    frequencyLabel: '随時',
  },
];

/**
 * テナントの theme_slug から業種を判定する。
 * バリアント（beauty-nail, corporate-it など）は接頭辞で基本業種に正規化する。
 */
export function normalizeIndustrySlug(themeSlug: string | null | undefined): string | null {
  if (!themeSlug) return null;
  const normalized = themeSlug.trim().toLowerCase();
  for (const industry of Object.keys(TARGETS_BY_INDUSTRY)) {
    if (normalized === industry || normalized.startsWith(`${industry}-`)) return industry;
  }
  return null;
}

export function quickUpdateTargetsForTheme(
  themeSlug: string | null | undefined,
): readonly QuickUpdateTarget[] {
  const industry = normalizeIndustrySlug(themeSlug);
  return industry ? (TARGETS_BY_INDUSTRY[industry] ?? FALLBACK_TARGETS) : FALLBACK_TARGETS;
}

/**
 * 業種別の対象定義とテナントのページ一覧を突合する。
 *
 * - 業種が判定できた場合: optional な対象だけブロックが無ければ隠す
 *   （必須対象は「まだサイトにない」案内付きで残す）。
 * - 業種不明の場合: 実際にブロックが存在する対象だけを表示する。
 */
export function matchQuickUpdateTargets(
  pages: readonly QuickUpdatePageSummary[],
  themeSlug: string | null | undefined,
): QuickUpdateMatch[] {
  const industry = normalizeIndustrySlug(themeSlug);
  const targets = quickUpdateTargetsForTheme(themeSlug);

  const matches = targets.map((target) => {
    const matched = pages.filter((page) =>
      page.blockTypes.some((blockType) => target.blockTypes.includes(blockType)),
    );
    const first = matched[0];
    return {
      target,
      pageId: first ? String(first.id) : null,
      blockType:
        first?.blockTypes.find((blockType) => target.blockTypes.includes(blockType)) ?? null,
      pageTitle: first?.title ?? null,
      pageCount: matched.length,
    };
  });

  return matches.filter((match) =>
    industry ? match.pageCount > 0 || !match.target.optional : match.pageCount > 0,
  );
}

/**
 * 「かんたん更新」エントリのワイヤ契約（admin ←→ CMS）。
 *
 * パスはどちらも CMS 管理画面相対（`/admin/...`）。admin 側は SSO ブリッジの
 * `_safePayloadAdminNext` が `/admin` 配下しか許容しないため、契約としても
 * `/admin` 始まりを必須にする。
 */
export const QuickUpdateEntrySummarySchema = z.object({
  /** 更新対象の安定 ID（例: medical-news, generic-menu） */
  id: z.string().min(1),
  /** 更新対象の名前（例: お知らせ・休診案内） */
  label: z.string().min(1),
  /** ボタンに表示する操作名（例: お知らせを編集する） */
  actionLabel: z.string().min(1),
  description: z.string(),
  /** 想定更新頻度の目安（カードに表示） */
  frequencyLabel: z.string(),
  /** 該当ブロックを含む最初のページのタイトル（見つからない場合は null） */
  pageTitle: z.string().nullable(),
  /** 該当ブロックを含むページ数。0 = まだサイトに掲載先がない */
  pageCount: z.number().int().nonnegative(),
  /** ページ編集画面のパス（例: /admin/collections/pages/21）。掲載先がなければ null */
  editPath: z.string().startsWith('/admin').nullable(),
  /**
   * クイック投稿フォームのパス（例: /admin/quick-update?page=21&block=news）。
   * 対象ブロックがクイック投稿に対応していなければ null。
   */
  quickFormPath: z.string().startsWith('/admin').nullable(),
});
export type QuickUpdateEntrySummary = z.infer<typeof QuickUpdateEntrySummarySchema>;

export const QuickUpdateEntriesResponseSchema = z.object({
  entries: z.array(QuickUpdateEntrySummarySchema),
});
export type QuickUpdateEntriesResponse = z.infer<typeof QuickUpdateEntriesResponseSchema>;
